import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  GetHomeConfigRequest, GetHomeConfigSlimIdRequest, GetHomeConfigSlimIdResponse, Home, HomeConfig, HomeConfigSlim,
  HomeIfcFile, HomeIfcFileType, ListHomesByCompanyRequest, ListIfcFilesByHomeIdRequest
} from 'situview-webapi-client';
import { FacadeHomeMatch, FacadeHomesDialogComponent, FacadeHomesDialogData } from '../components/facade-homes-dialog/facade-homes-dialog.component';
import { HomeService } from './home.service';

@Injectable({
  providedIn: 'root'
})
export class FacadeHomesService {
  constructor(
    private dialog: MatDialog,
    private homeService: HomeService) { }

  /**
   * Finds every home that has the given config's master file (facade), lets the user confirm which
   * to include, and returns a home config id per selected home - ready for the release dialog.
   * Returns undefined if the user cancels.
   * includePlaceholders should match the page's home picker - placeholders are a situFACADE-only concept.
   */
  async selectHomeConfigIdsForFacade(companyId: string, homeConfigId: string, includePlaceholders: boolean): Promise<FacadeHomeConfigIdsResult | undefined> {
    const homeConfigRequest = new GetHomeConfigRequest();
    homeConfigRequest.homeConfigId = homeConfigId;

    // No homeId - lists every IFC file in the company in one call
    const ifcFilesRequest = new ListIfcFilesByHomeIdRequest();
    ifcFilesRequest.companyId = companyId;

    const homesRequest = new ListHomesByCompanyRequest();
    homesRequest.companyId = companyId;

    let [homeConfigResponse, ifcFilesResponse, homes] = await Promise.all([
      this.homeService.GetHomeConfig(homeConfigRequest),
      this.homeService.ListIfcFilesByHomeId(ifcFilesRequest),
      this.homeService.ListHomesByCompany(homesRequest)
    ]);

    const currentConfig = homeConfigResponse?.homeConfig;
    if (!homeConfigResponse?.success || !currentConfig?.masterFile || !ifcFilesResponse?.success) {
      throw new Error("Failed to find homes for this facade.");
    }

    let ifcFiles = ifcFilesResponse.ifcFiles ?? [];
    if (!includePlaceholders) {
      ifcFiles = ifcFiles.filter(x => !x.placeholderArtifactId);
      homes = homes.filter(x => !x.isPlaceholder);
    }

    const matches = this.findFacadeHomeMatches(currentConfig, ifcFiles, homes);
    if (matches.length < 1) {
      throw new Error(`No homes found with master file ${currentConfig.masterFile}.`);
    }

    const selectedMatches: FacadeHomeMatch[] | undefined = await this.dialog.open(FacadeHomesDialogComponent, {
      panelClass: 'modal-panel',
      width: '60%',
      maxHeight: '80%',
      data: {
        masterFile: currentConfig.masterFile,
        options: currentConfig.options ?? [],
        matches: matches
      } as FacadeHomesDialogData
    }).afterClosed().toPromise();

    if (!selectedMatches || selectedMatches.length < 1) {
      return undefined;
    }

    const homeConfigIds = await Promise.all(selectedMatches.map(match => {
      const homeConfigSlim = new HomeConfigSlim();
      homeConfigSlim.companyId = companyId;
      homeConfigSlim.home = match.homeName;
      homeConfigSlim.masterFile = match.masterFile;
      homeConfigSlim.options = match.options;

      const request = new GetHomeConfigSlimIdRequest();
      request.companyId = companyId;
      request.homeConfig = homeConfigSlim;

      return this.homeService.GetHomeConfigId(request)
        .then((response: GetHomeConfigSlimIdResponse) => response?.success ? response.homeConfigId : undefined)
        .catch(() => undefined);
    }));

    const validHomeConfigIds = homeConfigIds.filter((id): id is string => !!id);
    return {
      homeConfigIds: validHomeConfigIds,
      failedCount: homeConfigIds.length - validHomeConfigIds.length
    };
  }

  private findFacadeHomeMatches(currentConfig: HomeConfig, ifcFiles: HomeIfcFile[], homes: Home[]): FacadeHomeMatch[] {
    const masterFile = currentConfig.masterFile!.toLowerCase();
    const currentOptions = currentConfig.options ?? [];
    const currentHome = currentConfig.home?.toLowerCase();

    const filesByHomeId = new Map<string, HomeIfcFile[]>();
    ifcFiles.forEach(file => {
      if (!file.homeId) {
        return;
      }
      const homeFiles = filesByHomeId.get(file.homeId) ?? [];
      homeFiles.push(file);
      filesByHomeId.set(file.homeId, homeFiles);
    });

    const matches: FacadeHomeMatch[] = [];
    homes.forEach(home => {
      const homeFiles = filesByHomeId.get(home.id ?? '') ?? [];
      const master = homeFiles.find(x => x.type === HomeIfcFileType.Master && x.name?.toLowerCase() === masterFile);
      if (!master || !home.name) {
        return;
      }

      // Carry the current options over only where this home has an option file of the same name
      const homeOptions = homeFiles.filter(x => x.type === HomeIfcFileType.Option);
      const options: string[] = [];
      const missingOptions: string[] = [];
      currentOptions.forEach(option => {
        const homeOption = homeOptions.find(x => x.name?.toLowerCase() === option.toLowerCase());
        if (homeOption?.name) {
          options.push(homeOption.name);
        } else {
          missingOptions.push(option);
        }
      });

      matches.push({
        homeName: home.name,
        masterFile: master.name!,
        options: options,
        missingOptions: missingOptions,
        isCurrentHome: home.name.toLowerCase() === currentHome,
        isPlaceholder: !!master.placeholderArtifactId,
        selected: true
      });
    });

    return matches.sort((a, b) => Number(b.isCurrentHome) - Number(a.isCurrentHome) || a.homeName.localeCompare(b.homeName));
  }
}

export interface FacadeHomeConfigIdsResult {
  homeConfigIds: string[];
  failedCount: number;
}
